import axios from 'axios'
import router from '@/router'
import Vue from 'vue'
import clearAllToasts from '@/utils/clearToasts'

const apiClient = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
})

apiClient.interceptors.request.use((request) => {
  const accessToken = localStorage.getItem('accessToken')
  if (accessToken) {
    request.headers.Authorization = `Bearer ${accessToken}`
  }
  return request
})

let isRedirectingToLogin = false
let lastForbiddenNotifiedAt = 0

apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const cfg = error.response && error.response.config
    // The Augustus/Linda proxy is under maintenance and intermittently returns
    // 401s that are NOT our session expiring. Don't drop the user's token or
    // bounce them to login for these — leave the existing session intact.
    const isAugustusProxy = cfg && cfg.url && cfg.url.indexOf('/augustus-proxy') === 0
    if (error.response && error.response.status === 401 && !isAugustusProxy) {
      localStorage.removeItem('accessToken')
      // Drop persisted permissions so a different user logging in on the same
      // browser doesn't briefly inherit the previous user's access map.
      try {
        // Lazy require to avoid circular import (axios ⇄ router ⇄ store).
        // eslint-disable-next-line global-require
        const store = require('@/store').default
        store.dispatch('permissions/CLEAR_PERMISSIONS')
        // The session is over, so say so: App-level pollers that run while
        // `authorized` (late-attendance) stop instead of 401-ing on the login
        // page every 15 minutes.
        store.commit('user/SET_STATE', { authorized: false })
      } catch (e) { /* store not ready yet */ }

      // A background request that outlived the session can 401 while the user
      // is already on the login page: nothing to redirect, and pushing the same
      // route throws NavigationDuplicated.
      const onLogin = router.currentRoute.path === '/auth/login'
      if (cfg && cfg.url != '/users/me' && !cfg.skipAuthRedirect && !isRedirectingToLogin && !onLogin) {
        isRedirectingToLogin = true
        // Toasts live on document.body, outside the router, so the old
        // session's toasts would follow the user onto the login page. Clear
        // them before saying why they are there.
        clearAllToasts()
        Vue.prototype.$notification.warning({
          message: 'Failed',
          description: 'Session has been expired. Please try again.',
        })
        router.push('/auth/login').catch(() => {})
        setTimeout(() => { isRedirectingToLogin = false }, 2000)
      }
    }
    if (error.response && error.response.status === 403 && !(cfg && cfg.skipPermissionToast)) {
      const now = Date.now()
      if (now - lastForbiddenNotifiedAt > 2000) {
        lastForbiddenNotifiedAt = now
        const description =
          (error.response.data && (error.response.data.message || error.response.data.error)) ||
          "You don't have access to this resource."
        Vue.prototype.$notification.warning({
          message: 'Permission Denied',
          description,
        })
      }
    }
    return Promise.reject(error)
  },
)

export default apiClient
